import React from 'react';
import { Briefcase, Heart, Award, GraduationCap, Code, Layout, LineChart, ArrowRight, Check } from 'lucide-react';
import Section from '../Section';
import Breadcrumb from '../common/Breadcrumb';
import { Link } from 'react-router-dom';

const Careers: React.FC = () => {
  const breadcrumbItems = [
    { label: '採用情報' }
  ];

  const positions = [
    {
      icon: Code,
      title: 'エンジニア職',
      value: 'engineer',
      description: 'フロントエンド、バックエンド、インフラなど、最新技術を活用した開発に携わります。',
      requirements: [
        'Web開発経験（実務2年以上）',
        'React/Vue.js等のモダンフレームワークの使用経験',
        'チーム開発の経験'
      ]
    },
    {
      icon: Layout,
      title: 'デザイナー職',
      value: 'designer',
      description: 'UI/UXデザインを中心に、ユーザー体験の向上に貢献します。',
      requirements: [
        'UIデザインの実務経験（2年以上）',
        'デザインツールの使用経験（Figma等）',
        'Webサイト/アプリのデザイン経験'
      ]
    },
    {
      icon: LineChart,
      title: 'プロジェクトマネージャー',
      value: 'pm',
      description: 'プロジェクトの進行管理、クライアントとの折衝を担当します。',
      requirements: [
        'IT業界でのPM経験（3年以上）',
        'アジャイル開発の知識・経験',
        'コミュニケーション能力'
      ]
    }
  ];

  const benefits = [
    {
      icon: GraduationCap,
      title: '教育・研修制度',
      items: [
        '新入社員研修プログラム',
        '技術勉強会の定期開催',
        '資格取得支援制度',
        '書籍購入補助'
      ]
    },
    {
      icon: Heart,
      title: '福利厚生',
      items: [
        '完全週休2日制',
        'フレックスタイム制',
        'リモートワーク可',
        '各種社会保険完備'
      ]
    },
    {
      icon: Award,
      title: '評価・報酬',
      items: [
        '年2回の昇給機会',
        '業績連動賞与',
        'スキル手当',
        '報奨金制度'
      ]
    }
  ];

  const steps = [
    { num: '1', title: '書類選考', desc: '履歴書、職務経歴書をご提出ください' },
    { num: '2', title: 'カジュアル面談', desc: 'オンラインで気軽な雰囲気で面談を行います' },
    { num: '3', title: '技術面接', desc: '実務経験やスキルについて詳しくお聞きします' },
    { num: '4', title: '最終面接', desc: '経営陣との面接を行います' },
  ];

  return (
    <div className="min-h-screen">
      {/* ヘッダーセクション */}
      <section className="relative pt-40 pb-24 overflow-hidden bg-[#1B2A4A]">
        <div className="absolute inset-0 bg-[#4A6FA5]/10" />
        <div className="relative mx-auto max-w-6xl px-5 lg:px-6">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/40 bg-[#B8860B]/10 px-4 py-1.5 mb-8">
              <Briefcase className="w-4 h-4 text-[#B8860B]" />
              <span className="text-sm font-semibold text-[#B8860B]">Join Our Team</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-[3.3rem] font-bold text-center text-[#F5F3EF] mb-6 leading-tight">
            採用情報
          </h1>

          <p className="text-lg text-[#F5F3EF]/80 text-center max-w-2xl mx-auto leading-relaxed">
            私たちと共に、テクノロジーの力で社会に新しい価値を創造しませんか？
            チャレンジ精神を持った仲間を募集しています。
          </p>
        </div>
      </section>

      <Breadcrumb items={breadcrumbItems} />

      {/* 募集職種セクション */}
      <Section className="py-20 lg:py-28 bg-[#F5F3EF]">
        <div className="mx-auto max-w-3xl px-5 lg:px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold text-[#B8860B]">募集職種</span>
            </div>
            <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">募集職種</h2>
            <p className="text-[#5A6577] mt-3">
              経験やスキルに応じて、最適なポジションでの採用を検討いたします
            </p>
          </div>

          <div className="space-y-6">
            {positions.map((position, index) => {
              const Icon = position.icon;
              return (
                <div key={index} className="rounded-2xl border border-[#E8E6E1] bg-white p-6 lg:p-8 shadow-sm">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1B2A4A]/10">
                      <Icon className="h-6 w-6 text-[#1B2A4A]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2D3748]">{position.title}</h3>
                  </div>
                  <p className="text-[#5A6577] mb-5 leading-relaxed text-justify">{position.description}</p>
                  <ul className="space-y-2.5 mb-6">
                    {position.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-center gap-2.5 text-[#5A6577]">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2D8659]/10">
                          <Check className="h-3 w-3 text-[#2D8659]" />
                        </div>
                        {req}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/careers/apply?position=${position.value}`}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#B8860B] px-6 py-3 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/20"
                  >
                    応募する
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 福利厚生セクション */}
      <Section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-3xl px-5 lg:px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold text-[#B8860B]">福利厚生</span>
            </div>
            <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">福利厚生</h2>
            <p className="text-[#5A6577] mt-3">
              社員一人一人が最大限の力を発揮できる環境を整えています
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="rounded-2xl border border-[#E8E6E1] bg-[#F5F3EF] p-6 shadow-sm">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1B2A4A]/10 mb-5">
                    <Icon className="h-6 w-6 text-[#1B2A4A]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2D3748] mb-4">{benefit.title}</h3>
                  <ul className="space-y-2.5">
                    {benefit.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2.5 text-[#5A6577] text-sm">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2D8659]/10">
                          <Check className="h-3 w-3 text-[#2D8659]" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 採用プロセスセクション */}
      <Section className="py-20 lg:py-28 bg-[#F5F3EF]">
        <div className="mx-auto max-w-3xl px-5 lg:px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold text-[#B8860B]">採用プロセス</span>
            </div>
            <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">採用プロセス</h2>
          </div>

          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6 lg:p-8 shadow-sm mb-10">
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div key={index} className={`flex items-center gap-4 ${index < steps.length - 1 ? 'pb-6 border-b border-[#E8E6E1]' : ''}`}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1B2A4A] text-white font-bold">
                    {step.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#2D3748] mb-1">{step.title}</h3>
                    <p className="text-[#5A6577] text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/careers/apply"
              className="inline-flex items-center gap-2 rounded-lg bg-[#B8860B] px-8 py-3 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/20"
            >
              応募する
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Careers;
